/**
 * MFA Enrollment Page
 *
 * Guides an admin through scanning the TOTP QR code and verifying the first
 * 6-digit code to activate their authenticator factor.
 * Accessible at /mfa/enroll — requires the user to be signed in (AAL1+).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';

export default function MfaEnroll() {
  const navigate = useNavigate();
  const { user, enrollTotp, verifyEnrollment, hasTotpFactor } = useAuth();

  const [step, setStep] = useState('loading'); // loading | qr | verify | done
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const startEnrollment = useCallback(async () => {
    setStep('loading');
    const result = await enrollTotp('MyMovement Coach');
    if (result.error) {
      toast.error('Enrollment failed', { description: result.error.message });
      setStep('qr');
      return;
    }
    setFactorId(result.factorId);
    setQrCode(result.qrCode);
    setSecret(result.secret);
    setStep('qr');
  }, [enrollTotp]);

  useEffect(() => {
    if (!user) return;
    if (hasTotpFactor) {
      // Already enrolled
      navigate('/');
      return;
    }
    startEnrollment();
  }, [user, hasTotpFactor, startEnrollment, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    const { error } = await verifyEnrollment(factorId, code);
    setLoading(false);
    if (error) {
      toast.error('Verification failed', { description: error.message });
      setCode('');
      return;
    }
    setStep('done');
    toast.success('Authenticator enrolled successfully');
    setTimeout(() => navigate('/'), 1500);
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>Set Up Authenticator — Coach</title>
      </Helmet>
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md shadow-none border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl font-semibold">Set Up Authenticator</CardTitle>
            <CardDescription>
              Scan the QR code with Google Authenticator or any TOTP app,
              then enter the 6-digit code to activate.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'loading' && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {step === 'qr' && (
              <div className="space-y-6">
                {qrCode && (
                  <div className="flex justify-center">
                    <img
                      src={qrCode}
                      alt="TOTP QR Code"
                      className="rounded-md border"
                      style={{ width: 200, height: 200 }}
                    />
                  </div>
                )}
                {secret && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      Can&apos;t scan? Enter this key manually:
                    </Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded bg-muted px-2 py-1.5 text-sm font-mono break-all">
                        {secret}
                      </code>
                      <Button variant="ghost" size="icon" onClick={copySecret} aria-label="Copy secret">
                        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="totp-code">Verification Code</Label>
                    <Input
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      autoComplete="one-time-code"
                      className="text-center text-lg tracking-widest"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Activate Authenticator
                  </Button>
                </form>
              </div>
            )}

            {step === 'done' && (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <ShieldCheck className="h-10 w-10 text-green-500" />
                <p className="font-medium">Authenticator activated</p>
                <p className="text-sm text-muted-foreground">Redirecting to dashboard…</p>
              </div>
            )}

            <p className="mt-4 text-xs text-muted-foreground text-center">
              Lost your device? Contact a project owner to remove the factor from
              the Supabase Authentication dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
